import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useDateIndex } from '@/lib/queries';
import { cn } from '@/lib/utils';

/**
 * Indice per data: per ogni anno la riga dei mesi 1–12.
 * I mesi con articoli sono link a {basePath}?anno=Y&mese=M; quelli vuoti sono in
 * grigio e non cliccabili. L'etichetta dell'anno è un link a tutto l'anno.
 * `basePath` permette di riusare l'indice fuori dalla home (es. /admin/articoli).
 *
 * La lista è chiusa di default ("a tendina"): si apre con il comando in testa,
 * per non ingombrare la schermata — soprattutto sul telefono.
 */
export function DateIndex({ basePath = '/' }: { basePath?: string } = {}) {
  const { data, isLoading, error } = useDateIndex();
  const index = data?.index ?? [];
  const [open, setOpen] = useState(false);

  if (isLoading && !data) {
    return <p className="py-12 text-center text-sm text-(--color-muted-foreground)">Caricamento indice…</p>;
  }
  if (error) {
    return <p className="py-12 text-center text-sm text-(--color-destructive)">Errore: {error.message}</p>;
  }
  if (index.length === 0) {
    return <p className="py-12 text-center text-sm text-(--color-muted-foreground)">Nessun articolo.</p>;
  }

  return (
    <section aria-label="Indice per data">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="indice-per-data"
        className="flex w-full items-center gap-1.5 font-serif text-lg font-semibold mb-3 hover:underline"
      >
        <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} />
        Sfoglia per anno
      </button>
      {!open ? null : (
      <ul id="indice-per-data" className="divide-y divide-(--color-border)">
        {index.map(({ anno, months }) => (
          <li key={anno} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5">
            <Link
              to={`${basePath}?anno=${anno}`}
              className="w-12 shrink-0 font-serif text-base font-semibold tabular-nums hover:underline"
            >
              {anno}
            </Link>
            <div className="flex flex-wrap gap-1">
              {months.map((count, i) => {
                const m = i + 1;
                if (count > 0) {
                  return (
                    <Link
                      key={m}
                      to={`${basePath}?anno=${anno}&mese=${m}`}
                      title={`${count} articol${count === 1 ? 'o' : 'i'}`}
                      className="rounded px-1.5 py-0.5 text-xs tabular-nums text-(--color-foreground) hover:bg-(--color-muted)"
                    >
                      {m}
                    </Link>
                  );
                }
                return (
                  <span
                    key={m}
                    aria-hidden
                    className="select-none rounded px-1.5 py-0.5 text-xs tabular-nums text-(--color-muted-foreground)/40"
                  >
                    {m}
                  </span>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      )}
    </section>
  );
}
